import axios, { AxiosError } from 'axios'
import { auth } from './firebase'
import toastService from './toastService'
import { getApiErrorMessage, markToastShown } from './apiError'
import { ACTING_COACH_ID_KEY, ACTING_COACH_HEADER } from '@/constants/viewAs'

const axiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_API_URL as string) || 'http://localhost:8080',
})

axiosInstance.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser
    if (user) {
      const token = await user.getIdToken()
      config.headers.Authorization = `Bearer ${token}`
    }
    // When an admin is "viewing as" a coach, carry the acting-coach id so
    // coach-scoped endpoints run as that coach (see ActingCoachMiddleware).
    // Read from localStorage to avoid a store import cycle.
    const actingCoachId = localStorage.getItem(ACTING_COACH_ID_KEY)
    if (actingCoachId) {
      config.headers[ACTING_COACH_HEADER] = actingCoachId
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    const backendMessage = getApiErrorMessage(error)

    if (status === 403) {
      toastService.error(backendMessage || 'You do not have permission to perform this action')
      markToastShown(error)
    } else if (status === 401) {
      toastService.error('Session expired. Please log in again.')
      markToastShown(error)
    } else if (status === 404) {
      // Don't show toast for 404 - let components handle it
    } else if (status === 422) {
      // Validation errors - let components handle it
    } else if (status && status >= 500) {
      toastService.error(backendMessage || 'Server error. Please try again later.')
      markToastShown(error)
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
